import { call, put, SagaReturnType, select, takeEvery } from 'redux-saga/effects';
import { doSucceededCreateOrder, doSucceededGetOrder, doSucceededGetOrderList } from 'src/actions';
import { OrderConstant } from 'src/constants';
import { getClientApi, OrdersApi, QuoteApi } from 'src/data-communication';
import { CancelOrderDto, CreateOrderDto, GetOrderDto, GetOrderListDto, OrderDto } from 'src/dtos';
import { mapGetOrdersListResponseDtoToModel, mapOrderDtoToModel } from 'src/mappers';
import { Order } from 'src/models';
import { getPayloadFromErrorResponse } from 'src/utils';

import { State, Type } from '../actions/utils';
import { TReduxAction } from '../typings/commonTypes';
import { Logger } from '../utils/Logger';

import { safeSaga } from './utils';

const findRemainingPages = ({ take, skip, total }: { take: number; skip: number; total: number }): number[] => {
  const result: number[] = [];
  const pages = Math.ceil(total / take);
  const nextPage = (skip + take) / take;

  for (let counter = nextPage; counter < pages; counter += 1) {
    result.push(counter);
  }

  return result;
};

const getRemainingOrderList = async ({
  pages,
  accountId,
  authToken,
  take,
}: {
  pages: number[];
  accountId: number;
  authToken: string;
  take: number;
}): Promise<OrderDto[]> => {
  try {
    const responses = await Promise.all(
      pages.map(aPage =>
        getClientApi().orders.list({
          params: { accountId },
          queryParams: {
            take,
            skip: aPage * take,
          },
          authToken,
        }),
      ),
    );

    return responses.reduce((all: OrderDto[], aResponse) => all.concat(aResponse.data), []);
  } catch (error) {
    Logger.error(`Error getting remaining order list:`, error);
    Logger.info('Fallback: use empty list');

    return [];
  }
};

export function* list(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const dto: GetOrderListDto & { fetchAll?: boolean } = action?.payload;

  const { take = OrderConstant.DEFAULT_TAKE_QUERY_PARAM, skip = OrderConstant.DEFAULT_SKIP_QUERY_PARAM } =
    dto.queryParams || {};
  let { data, total }: SagaReturnType<OrdersApi['list']> = yield call(getClientApi().orders.list, {
    params: dto.params,
    queryParams: {
      ...dto.queryParams,
      take,
      skip,
    },
    authToken,
  });

  if (dto.fetchAll) {
    const remainingOrders: SagaReturnType<typeof getRemainingOrderList> = yield call(getRemainingOrderList, {
      pages: findRemainingPages({ total, take, skip }),
      authToken,
      accountId: dto.params.accountId,
      take,
    });
    data = data.concat(remainingOrders);
  }

  const symbols: string[] = data.reduce((all: string[], current) => {
    if (current.UnderlyingSymbol) {
      all.push(current.UnderlyingSymbol);
    }

    return all;
  }, []);
  let quotes: SagaReturnType<QuoteApi['list']> = { quotedata: [] };

  try {
    quotes = yield call(getClientApi().quote.list, { queryParams: { symbols: symbols.join(',') }, authToken });
  } catch (error) {
    Logger.error(`Error getting quote media snap list:`, error);
    Logger.info('Fallback: use empty quote media snap list');
  }
  const orders: Order[] = mapGetOrdersListResponseDtoToModel(data, quotes.quotedata);
  yield put(doSucceededGetOrderList({ data: orders, total }));
}

export function* retrieve(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const dto: GetOrderDto = action?.payload;
  const order: SagaReturnType<OrdersApi['retrieve']> = yield call(getClientApi().orders.retrieve, {
    ...dto,
    authToken,
  });

  let quotes: SagaReturnType<QuoteApi['list']> = { quotedata: [] };

  try {
    if (order.UnderlyingSymbol) {
      quotes = yield call(getClientApi().quote.list, { queryParams: { symbols: order.UnderlyingSymbol }, authToken });
    }
  } catch (error) {
    Logger.error(`Error getting quote media snap list:`, error);
    Logger.info('Fallback: use empty quote snap list');
  }
  const newOrder: Order = mapOrderDtoToModel(order, quotes.quotedata[0]);
  yield put(doSucceededGetOrder(newOrder));
}

export function* create(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: CreateOrderDto = action?.payload;
    const newOrder: SagaReturnType<OrdersApi['create']> = yield call(getClientApi().orders.create, {
      ...dto,
      authToken,
    });

    let quotes: SagaReturnType<QuoteApi['list']> = { quotedata: [] };

    try {
      if (newOrder.UnderlyingSymbol) {
        quotes = yield call(getClientApi().quote.list, {
          queryParams: { symbols: newOrder.UnderlyingSymbol },
          authToken,
        });
      }
    } catch (error) {
      Logger.error(`Error getting quote media snap list for new order(${newOrder.UnderlyingSymbol}):`, error);
    }

    const newOrderModel = mapOrderDtoToModel(newOrder, quotes.quotedata[0]);

    yield put(doSucceededCreateOrder(newOrderModel));

    const orderList: Order[] | undefined = yield select(state => state.orders.list.data?.data);
    const total: number | undefined = yield select(state => state.orders.list.data?.total);

    if (orderList && total !== undefined) {
      yield put(doSucceededGetOrderList({ data: [...orderList, newOrderModel], total: total + 1 }));
    }
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.CREATE_ORDER),
      payload,
    });
  }
}

export function* cancel(action: TReduxAction) {
  try {
    const { authToken } = yield select(state => state.auth.data);
    const dto: CancelOrderDto = action?.payload;
    yield call(getClientApi().orders.cancel, { ...dto, authToken });
    yield put({
      type: State.actionSucceeded(Type.CANCEL_ORDER),
    });
  } catch (error) {
    const payload = getPayloadFromErrorResponse(error);
    yield put({
      type: State.actionFailed(Type.CANCEL_ORDER),
      payload,
    });
  }
}

/**
 * Orders sagas
 */
export default function* orderSagas() {
  yield takeEvery(State.actionRequested(Type.GET_ORDERS), safeSaga(list, Type.GET_ORDERS));
  yield takeEvery(State.actionRequested(Type.GET_ORDERS_ID), safeSaga(retrieve, Type.GET_ORDERS_ID));
  yield takeEvery(State.actionRequested(Type.CREATE_ORDER), create);
  yield takeEvery(State.actionRequested(Type.CANCEL_ORDER), cancel);
}
